import { Button, DatePicker, Form, InputNumber, Typography } from 'antd';
import { IconCoins, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { OemRateWindowUnit } from '@evimo/types/enums/oem-rate-window-unit';
import { SlaCurrencySelect } from '~/app/landing/sla/shared/components/SlaCurrencySelect';
import { SlaFormField } from '~/app/landing/sla/shared/components/SlaFormField';
import { SlaLabeledSelect } from '~/app/landing/sla/shared/components/SlaLabeledSelect';
import { SlaModalFieldGrid } from '~/app/landing/sla/shared/components/SlaModalFieldGrid';
import { SLA_OEM_RATE_MATRIX_SCROLL_Y } from '~/app/landing/sla/shared/sla.constants';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import {
  SLA_RATE_WINDOW_UNIT_SELECT_OPTIONS,
  SLA_STATUS_SELECT_OPTIONS,
} from '~/app/landing/sla/shared/utils/sla-select-options';

type Props = {
  maxRateEntries?: number;
};

export function OemPartnerRateMatrix({ maxRateEntries }: Props) {
  const { t } = useTranslation('sla');

  return (
    <>
      <SlaModalFieldGrid columns={3}>
        <SlaFormField
          name="validFrom"
          label={t('oems.fields.validFrom')}
          rules={[{ required: true, message: t('validation.required') }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </SlaFormField>
        <SlaFormField name="validTo" label={t('oems.fields.validTo')}>
          <DatePicker style={{ width: '100%' }} />
        </SlaFormField>
        <SlaFormField
          name="status"
          label={t('oems.fields.status')}
          rules={[{ required: true, message: t('validation.required') }]}
        >
          <SlaLabeledSelect
            style={{ width: '100%' }}
            options={SLA_STATUS_SELECT_OPTIONS}
            translationPrefix="status"
          />
        </SlaFormField>
      </SlaModalFieldGrid>

      <Typography.Text
        className="ds-section-header"
        style={{
          display: 'block',
          marginTop: '1.25rem',
          marginBottom: '0.75rem',
        }}
      >
        {t('oems.rateMatrixTitle')}
      </Typography.Text>

      <Form.List name="rateEntries">
        {(fields, { add, remove }) => {
          const atMax =
            maxRateEntries != null && fields.length >= maxRateEntries;

          return (
            <>
              <div className="sla-oem-rate-matrix-box">
                <EvimoTable
                  pagination={false}
                  dataSource={fields}
                  rowKey="key"
                  size="small"
                  scroll={{ y: SLA_OEM_RATE_MATRIX_SCROLL_Y }}
                  columns={[
                    {
                      title: t('oems.fields.windowValue'),
                      width: '4.5rem',
                      render: (_, field) => (
                        <SlaFormField
                          name={[field.name, 'windowValue']}
                          rules={[
                            {
                              required: true,
                              message: t('validation.required'),
                            },
                          ]}
                          inline
                        >
                          <InputNumber
                            min={0.01}
                            style={{ width: '100%' }}
                            controls={false}
                          />
                        </SlaFormField>
                      ),
                    },
                    {
                      title: t('oems.fields.windowUnit'),
                      width: '6.5rem',
                      render: (_, field) => (
                        <SlaFormField
                          name={[field.name, 'windowUnit']}
                          rules={[
                            {
                              required: true,
                              message: t('validation.required'),
                            },
                          ]}
                          inline
                        >
                          <SlaLabeledSelect
                            options={SLA_RATE_WINDOW_UNIT_SELECT_OPTIONS}
                            translationPrefix="rateWindowUnit"
                          />
                        </SlaFormField>
                      ),
                    },
                    {
                      title: t('oems.fields.cost'),
                      width: '7.5rem',
                      render: (_, field) => (
                        <SlaFormField
                          name={[field.name, 'costAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('validation.required'),
                            },
                          ]}
                          inline
                        >
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </SlaFormField>
                      ),
                    },
                    {
                      title: (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <IconCoins size={14} />
                          {t('oems.fields.currency')}
                        </span>
                      ),
                      width: '12rem',
                      render: (_, field) => (
                        <SlaFormField
                          name={[field.name, 'currency']}
                          rules={[
                            {
                              required: true,
                              message: t('validation.required'),
                            },
                          ]}
                          inline
                        >
                          <SlaCurrencySelect className="sla-rate-matrix-currency" />
                        </SlaFormField>
                      ),
                    },
                    {
                      title: '',
                      width: '6rem',
                      render: (_, field) => (
                        <Button danger onClick={() => remove(field.name)}>
                          {t('actions.remove')}
                        </Button>
                      ),
                    },
                  ]}
                />
              </div>
              <Button
                type="dashed"
                icon={<IconPlus size={16} />}
                disabled={atMax}
                onClick={() => {
                  if (atMax) {
                    return;
                  }
                  add({
                    windowValue: 1,
                    windowUnit: OemRateWindowUnit.DAY,
                    costAmount: 0,
                    currency: 'EUR',
                  });
                }}
                style={{ marginTop: '0.75rem' }}
                block
              >
                {t('oems.addRate')}
              </Button>
              {atMax ? (
                <Typography.Text
                  type="secondary"
                  style={{
                    display: 'block',
                    marginTop: '0.5rem',
                    fontSize: 12,
                  }}
                >
                  {t('oems.maxRatesReached', { max: maxRateEntries })}
                </Typography.Text>
              ) : null}
            </>
          );
        }}
      </Form.List>
    </>
  );
}
